'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useAuth, UserRole } from '@/contexts/AuthContext';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { ROUTE_ROLE_MAP } from './RouteGuard';

const PM_SHORTCUTS: Record<number, string> = {
  1: '/project-manager-dashboard',
  2: '/interactive-calendar-schedule-management',
};

interface NavItem {
  label: string;
  path: string;
  icon: string;
  isActive: boolean;
  comingSoon: boolean;
  requiredRoles?: UserRole[];
}

const navItems: NavItem[] = [
  { label: 'VPM Dashboard', path: '/project-manager-dashboard', icon: 'Squares2X2Icon', isActive: true, comingSoon: false, requiredRoles: ROUTE_ROLE_MAP['/project-manager-dashboard'] },
  { label: 'Sales Dashboard', path: '/sales-dashboard', icon: 'PresentationChartLineIcon', isActive: true, comingSoon: false, requiredRoles: ROUTE_ROLE_MAP['/sales-dashboard'] },
  { label: 'Schedule', path: '/interactive-calendar-schedule-management', icon: 'CalendarDaysIcon', isActive: true, comingSoon: false },
  { label: 'Executive', path: '/executive-command-center-dashboard', icon: 'ChartBarSquareIcon', isActive: true, comingSoon: false, requiredRoles: ROUTE_ROLE_MAP['/executive-command-center-dashboard'] },
  { label: 'Financial', path: '/financial-accounting-dashboard', icon: 'BanknotesIcon', isActive: true, comingSoon: false, requiredRoles: ROUTE_ROLE_MAP['/financial-accounting-dashboard'] },
  { label: 'Operations', path: '/operations-command-center-dashboard', icon: 'CogIcon', isActive: true, comingSoon: false, requiredRoles: ROUTE_ROLE_MAP['/operations-command-center-dashboard'] },
  { label: 'Logistics', path: '/logistics-supply-chain-dashboard', icon: 'TruckIcon', isActive: true, comingSoon: false, requiredRoles: ROUTE_ROLE_MAP['/logistics-supply-chain-dashboard'] },
  { label: 'HR', path: '/hr', icon: 'UserGroupIcon', isActive: true, comingSoon: false, requiredRoles: ROUTE_ROLE_MAP['/hr'] },
  { label: 'Storm Map', path: '/storm-map', icon: 'CloudIcon', isActive: true, comingSoon: false, requiredRoles: ROUTE_ROLE_MAP['/storm-map'] },
  { label: 'Expansion', path: '/expansion-market-intelligence-dashboard', icon: 'ArrowTrendingUpIcon', isActive: true, comingSoon: false, requiredRoles: ROUTE_ROLE_MAP['/expansion-market-intelligence-dashboard'] },
  { label: 'Installers', path: '/installer-network-crew-management-dashboard', icon: 'WrenchScrewdriverIcon', isActive: true, comingSoon: false, requiredRoles: ROUTE_ROLE_MAP['/installer-network-crew-management-dashboard'] },
  { label: 'Reports', path: '/reports-analytics-dashboard', icon: 'ChartBarIcon', isActive: true, comingSoon: false, requiredRoles: ROUTE_ROLE_MAP['/reports-analytics-dashboard'] },
  { label: 'Warranty', path: '/warranty-claims-dashboard', icon: 'ShieldCheckIcon', isActive: true, comingSoon: false, requiredRoles: ROUTE_ROLE_MAP['/warranty-claims-dashboard'] },
  { label: 'Escalations', path: '/escalation-management-dashboard', icon: 'ExclamationTriangleIcon', isActive: true, comingSoon: false, requiredRoles: ROUTE_ROLE_MAP['/escalation-management-dashboard'] },
  { label: 'Employees', path: '/employee-directory', icon: 'UsersIcon', isActive: true, comingSoon: false, requiredRoles: ROUTE_ROLE_MAP['/employee-directory'] },
  { label: 'Boards', path: '/customer-dashboard', icon: 'ViewColumnsIcon', isActive: true, comingSoon: false, requiredRoles: ROUTE_ROLE_MAP['/customer-dashboard'] },
  { label: 'Customers', path: '/customers', icon: 'UsersIcon', isActive: true, comingSoon: false, requiredRoles: ROUTE_ROLE_MAP['/customers'] },
  { label: 'Contacts', path: '/contacts', icon: 'IdentificationIcon', isActive: true, comingSoon: false, requiredRoles: ROUTE_ROLE_MAP['/contacts'] },
];

const getNavItems = (hasAnyRole: (roles: UserRole[]) => boolean, isSuperAdmin: boolean): NavItem[] => {
  if (isSuperAdmin) {
    const seen = new Set<string>();
    return navItems.filter(item => {
      if (seen.has(item.path + item.label)) return false;
      seen.add(item.path + item.label);
      return true;
    });
  }
  return navItems.filter((item) => {
    if (!item.requiredRoles) return true;
    return hasAnyRole(item.requiredRoles);
  });
};

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const Sidebar = ({ isOpen = false, onClose }: SidebarProps) => {
  const pathname = usePathname();
  const router = useRouter();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const { currentUser, hasAnyRole, isSuperAdmin } = useAuth();
  const { isImpersonating, impersonatedUser, getEffectiveRoles, getEffectiveIsSuperAdmin } = useImpersonation();

  // Keyboard shortcuts: Alt+1 → Dashboard, Alt+2 → Schedule
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.altKey) return;
      const num = parseInt(e.key, 10);
      if (num >= 1 && num <= 2 && PM_SHORTCUTS[num]) {
        e.preventDefault();
        router.push(PM_SHORTCUTS[num]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [router]);

  // The three pipeline boards share one "Boards" nav item (→ /customer-dashboard);
  // keep it highlighted on any of them.
  const BOARD_PATHS = ['/customer-dashboard', '/production-board', '/billing-board'];

  const isItemActive = (item: NavItem) => {
    if (item.comingSoon) return false;
    if (item.path === '/customer-dashboard') return BOARD_PATHS.includes(pathname);
    return pathname === item.path;
  };

  // Use effective roles (impersonated user's roles if in preview mode)
  const effectiveRoles = getEffectiveRoles(currentUser.roles);
  const effectiveIsSuperAdmin = getEffectiveIsSuperAdmin(isSuperAdmin);

  const effectiveHasAnyRole = (roles: UserRole[]): boolean => {
    if (effectiveIsSuperAdmin) return true;
    return roles.some(r => effectiveRoles.includes(r));
  };

  const visibleNavItems = getNavItems(effectiveHasAnyRole, effectiveIsSuperAdmin);

  // Display info: show impersonated user if in preview mode
  const displayUser = isImpersonating && impersonatedUser
    ? impersonatedUser
    : currentUser;

  const roleBadges = displayUser.roles.slice(0, 2);
  const extraRolesCount = displayUser.roles.length - 2;

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-nav lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full w-sidebar bg-gray-50 border-r border-gray-200 z-nav
          flex flex-col overflow-hidden
          transition-transform duration-[250ms] ease-standard
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
        aria-label="Primary navigation"
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-gray-200 flex-shrink-0">
          <div
            className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #1C2E5E 0%, #E89A3C 100%)' }}
          >
            <span className="font-heading font-700 text-white text-sm leading-none tracking-tight">NR</span>
          </div>
          <div className="flex flex-col">
            <span className="font-heading font-semibold text-base leading-tight tracking-wide">
              <span style={{ color: '#1C2E5E' }}>NextRoof</span> <span style={{ color: '#E89A3C' }}>360</span>
            </span>
            <span className="font-caption text-slate-500 text-xs tracking-wider uppercase">
              Roofing Pro
            </span>
          </div>
          {/* Close button mobile */}
          <button
            className="ml-auto lg:hidden text-slate-500 hover:text-slate-700 transition-standard p-1 rounded-sm"
            onClick={onClose}
            aria-label="Close navigation"
          >
            <Icon name="XMarkIcon" size={20} />
          </button>
        </div>

        {/* Role badge */}
        <div className="px-5 py-2.5 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${isImpersonating ? 'bg-amber-100' : 'bg-blue-100'}`}>
              <span className="font-heading text-[10px] font-bold text-slate-700">
                {displayUser.first_name?.charAt(0)}{displayUser.last_name?.charAt(0)}
              </span>
            </div>
            <div className="flex flex-col min-w-0">
              <span className="font-body text-xs text-slate-700 truncate">
                {isImpersonating ? (
                  <span className="text-amber-600">{displayUser.first_name} {displayUser.last_name}</span>
                ) : (
                  <>{displayUser.first_name} {displayUser.last_name}</>
                )}
              </span>
              <div className="flex items-center gap-1 flex-wrap mt-0.5">
                {isImpersonating && (
                  <span className="font-caption text-[9px] text-amber-600 bg-amber-50 px-1 py-0.5 rounded-sm leading-tight">
                    Preview
                  </span>
                )}
                {roleBadges.map((r) => (
                  <span key={r} className="font-caption text-[9px] text-slate-500 bg-gray-100 px-1 py-0.5 rounded-sm leading-tight truncate">
                    {r.replace(/_/g, ' ')}
                  </span>
                ))}
                {extraRolesCount > 0 && (
                  <span className="font-caption text-[9px] text-slate-500 leading-tight">+{extraRolesCount}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-4 px-3" role="navigation">
          <ul className="space-y-1" role="list">
            {visibleNavItems.map((item) => {
              const active = isItemActive(item);
              const hovered = hoveredItem === item.label;
              const shortcutNum = Object.entries(PM_SHORTCUTS).find(([, path]) => path === item.path)?.[0];

              return (
                <li key={item.label} role="listitem">
                  {item.comingSoon ? (
                    <div
                      className="relative flex items-center gap-3 px-3 py-2.5 rounded-md cursor-not-allowed group"
                      onMouseEnter={() => setHoveredItem(item.label)}
                      onMouseLeave={() => setHoveredItem(null)}
                      aria-disabled="true"
                    >
                      <Icon
                        name={item.icon as any}
                        size={20}
                        className="text-slate-300 flex-shrink-0"
                        variant="outline"
                      />
                      <span className="font-body text-sm font-normal text-slate-300 flex-1 truncate">
                        {item.label}
                      </span>
                      <span className="font-caption text-[10px] text-slate-400 bg-gray-100 px-1.5 py-0.5 rounded-sm leading-tight flex-shrink-0">
                        Soon
                      </span>
                      {hovered && (
                        <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 bg-white text-slate-700 text-xs font-caption px-2.5 py-1.5 rounded-md shadow-elevated whitespace-nowrap z-50 pointer-events-none border border-gray-200">
                          Coming soon
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      href={item.path}
                      onClick={onClose}
                      className={`
                        relative flex items-center gap-3 px-3 py-2.5 rounded-md
                        transition-standard press-scale
                        ${active
                          ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600 pl-2' :'text-slate-700 font-normal hover:bg-gray-100 hover:text-slate-900 border-l-4 border-transparent pl-2'
                        }
                      `}
                      aria-current={active ? 'page' : undefined}
                    >
                      <Icon
                        name={item.icon as any}
                        size={20}
                        className={`flex-shrink-0 ${active ? 'text-blue-600' : 'text-slate-700'}`}
                        variant={active ? 'solid' : 'outline'}
                      />
                      <span className={`font-body text-sm flex-1 truncate ${active ? 'font-semibold text-blue-600' : 'font-normal text-slate-700'}`}>
                        {item.label}
                      </span>
                      {shortcutNum && (
                        <span
                          className={`font-caption text-[10px] px-1.5 py-0.5 rounded-sm leading-tight flex-shrink-0 border ${
                            active
                              ? 'bg-blue-100 text-blue-600 border-blue-200' :'bg-gray-100 text-slate-500 border-gray-200'
                          }`}
                          title={`Alt+${shortcutNum}`}
                        >
                          ⌥{shortcutNum}
                        </span>
                      )}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Bottom section */}
        <div className="flex-shrink-0 px-3 py-4 border-t border-gray-200">
          <Link
            href="/role-based-navigation-module-management"
            onClick={onClose}
            className={`
              relative flex items-center gap-3 px-3 py-2.5 rounded-md transition-standard press-scale border-l-4 pl-2
              ${pathname === '/role-based-navigation-module-management' ?'bg-blue-50 text-blue-600 border-blue-600' :'text-slate-700 hover:bg-gray-100 hover:text-slate-900 border-transparent'}
            `}
          >
            <Icon name="UsersIcon" size={20} className={`flex-shrink-0 ${pathname === '/role-based-navigation-module-management' ? 'text-blue-600' : 'text-slate-700'}`} variant="outline" />
            <span className={`font-body text-sm flex-1 truncate ${pathname === '/role-based-navigation-module-management' ? 'font-semibold text-blue-600' : 'font-normal text-slate-700'}`}>Role Management</span>
          </Link>
          <div className="mt-3 px-3">
            <p className="font-caption text-slate-400 text-xs">
              v2.4.1 · Mar 2026
            </p>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;